import { useState } from 'react'
import { Copy, Link2, MoreHorizontal, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  useListProductComponents,
  type ProductBomImportResult,
  type ProductComponentLink,
} from '@/shared/hooks/api/catalog'
import { useGetProduct } from '@/shared/hooks/api/product'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { V2ClearProductPartsDialog } from '../components/V2ClearProductPartsDialog'
import { V2ImportBomButton } from '../components/V2ImportBomButton'
import { V2LinkProductComponentDialog } from '../components/V2LinkProductComponentDialog'
import { V2RemoveProductPartDialog } from '../components/V2RemoveProductPartDialog'
import { V2TableLink } from '../components/V2TableLink'
import { getV2ComponentDetailPath } from '../utils/routes'

type V2ProductPartsPageProps = {
  productId: string
  initialImportFailures?: ProductBomImportResult['failed']
}

export function V2ProductPartsPage({
  productId,
  initialImportFailures = [],
}: V2ProductPartsPageProps) {
  const { t } = useTranslation('common')
  const { data: product, isLoading: isLoadingProduct } = useGetProduct(productId)
  const {
    data: productComponents = [],
    isLoading: isLoadingComponents,
    isError: isComponentsError,
  } = useListProductComponents(productId)
  const [linkDialogOpen, setLinkDialogOpen] = useState(false)
  const [clearDialogOpen, setClearDialogOpen] = useState(false)
  const [partToRemove, setPartToRemove] = useState<ProductComponentLink | null>(
    null
  )
  const [importFailures, setImportFailures] = useState(initialImportFailures)

  const showPartsEmpty =
    !isLoadingComponents && productComponents.length === 0
  const showPartsTable =
    !isLoadingComponents && productComponents.length > 0
  const showClearAllButton = productComponents.length > 0
  const title =
    product?.name?.trim() || t('v2.pages.products.fallbackTitle')
  const skuLabel =
    product?.sku != null && product.sku.trim() !== ''
      ? product.sku.trim()
      : null

  async function copySupplierInviteLink(magicLinkUrl: string) {
    await navigator.clipboard.writeText(magicLinkUrl)
    toast.success(t('v2.pages.suppliers.copyInviteLinkSuccess'))
  }

  return (
    <div className="space-y-6">
      <header className="space-y-3">
        {isLoadingProduct ? (
          <>
            <Skeleton className="h-8 w-64 max-w-full" />
            <Skeleton className="h-4 w-32" />
          </>
        ) : (
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="space-y-1">
              <h1 className="text-2xl font-bold tracking-tight">{title}</h1>
              {skuLabel != null && (
                <p className="text-muted-foreground">
                  {t('v2.pages.products.detail.skuLabel', { sku: skuLabel })}
                </p>
              )}
              <p className="text-sm text-muted-foreground">
                {t('v2.pages.products.detail.parts.heading')}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <V2ImportBomButton
                productId={productId}
                onImported={(result) => {
                  setImportFailures(result.failed)
                }}
              />
              <Button
                className="gap-2"
                onClick={() => {
                  setLinkDialogOpen(true)
                }}
                size="sm"
                type="button"
                variant="outline"
              >
                <Link2 className="size-4" />
                {t('v2.pages.products.detail.parts.linkButton')}
              </Button>
              {showClearAllButton && (
                <Button
                  size="sm"
                  type="button"
                  variant="destructive"
                  onClick={() => {
                    setClearDialogOpen(true)
                  }}
                >
                  {t('v2.pages.products.detail.parts.clear.button')}
                </Button>
              )}
            </div>
          </div>
        )}
      </header>

      <section className="space-y-3">
        {importFailures.length > 0 && (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm">
            <p className="font-medium text-destructive">
              {t('v2.pages.products.detail.parts.import.failedHeading', {
                count: importFailures.length,
              })}
            </p>
            <ul className="mt-2 space-y-1 text-destructive">
              {importFailures.map((failure) => {
                const failureKey =
                  failure.sku != null && failure.sku.trim() !== ''
                    ? 'v2.pages.products.detail.parts.import.failedRowWithSku'
                    : 'v2.pages.products.detail.parts.import.failedRow'

                return (
                  <li key={`${failure.row}-${failure.reason}`}>
                    {t(failureKey, {
                      row: failure.row,
                      reason: failure.reason,
                      sku: failure.sku,
                    })}
                  </li>
                )
              })}
            </ul>
          </div>
        )}

        {isLoadingComponents && (
          <div className="space-y-2" aria-busy="true">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        )}

        {showPartsEmpty && (
          <Empty className="border border-dashed bg-muted/20">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <Link2 aria-hidden />
              </EmptyMedia>
              <EmptyTitle>
                {t('v2.pages.products.detail.parts.empty.title')}
              </EmptyTitle>
              <EmptyDescription>
                {t('v2.pages.products.detail.parts.empty.description')}
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        )}

        {showPartsTable && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  {t('v2.pages.products.detail.parts.columns.sku')}
                </TableHead>
                <TableHead>
                  {t('v2.pages.products.detail.parts.columns.name')}
                </TableHead>
                <TableHead>
                  {t('v2.pages.products.detail.parts.columns.supplier')}
                </TableHead>
                <TableHead className="w-[100px] text-end">
                  {t('v2.pages.products.detail.parts.columns.actions')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {productComponents.map((row) => {
                const componentName =
                  row.component.name != null &&
                  row.component.name.trim() !== ''
                    ? row.component.name.trim()
                    : t('v2.pages.components.fallbackName')

                return (
                  <TableRow key={row.componentId}>
                    <TableCell>
                      <V2TableLink to={getV2ComponentDetailPath(row.componentId)}>
                        {row.component.sku}
                      </V2TableLink>
                    </TableCell>
                    <TableCell>
                      <V2TableLink to={getV2ComponentDetailPath(row.componentId)}>
                        {componentName}
                      </V2TableLink>
                    </TableCell>
                    <TableCell>{row.component.supplier.name}</TableCell>
                    <TableCell className="text-end">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="size-8 shrink-0"
                            aria-label={t(
                              'v2.pages.products.detail.parts.actionsMenuAriaLabel',
                              {
                                sku: row.component.sku,
                                name: componentName,
                              }
                            )}
                          >
                            <MoreHorizontal className="size-4" aria-hidden />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => {
                              void copySupplierInviteLink(
                                row.component.supplier.magicLinkUrl
                              )
                            }}
                          >
                            <Copy className="size-4" aria-hidden />
                            {t('v2.pages.suppliers.copyInviteLink')}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            variant="destructive"
                            onClick={() => {
                              setPartToRemove(row)
                            }}
                          >
                            <Trash2 className="size-4" aria-hidden />
                            {t('v2.pages.products.detail.parts.remove.button')}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}

        {isComponentsError && (
          <p className="text-sm text-destructive">
            {t('notifications.unknownError')}
          </p>
        )}
      </section>

      <V2LinkProductComponentDialog
        productId={productId}
        open={linkDialogOpen}
        onOpenChange={setLinkDialogOpen}
      />

      <V2RemoveProductPartDialog
        productId={productId}
        part={partToRemove}
        open={partToRemove != null}
        onOpenChange={(open) => {
          if (!open) {
            setPartToRemove(null)
          }
        }}
      />

      <V2ClearProductPartsDialog
        productId={productId}
        partCount={productComponents.length}
        open={clearDialogOpen}
        onOpenChange={setClearDialogOpen}
      />
    </div>
  )
}
